import { Fragment } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, User } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { useNetwork } from '@/contexts/NetworkContext'
import { UsersApi, type NetworkUser, type UserRole } from '@/services/users/usersApi'

interface RoleUsersCardProps {
  roleId: string
  application: string
}

interface Holder {
  user: NetworkUser
  /** Where the user holds the role: network-wide, or on teams and team groups. */
  held: UserRole[]
}

/** Masked usernames, as shown for users of other networks, can't be opened. */
const isMasked = (username: string) => !username.includes('@') && username.indexOf('*') > 0

/** The users of the network that hold a role, and where they hold it. */
export function RoleUsersCard({ roleId, application }: RoleUsersCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentNetwork } = useNetwork()

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['role-users', currentNetwork?.tagId, application],
    queryFn: async () =>
      (await UsersApi.getAll({ app: application, includeInactive: true })).results,
    enabled: !!currentNetwork && !!application,
  })

  const holders: Holder[] = []
  for (const user of users) {
    const networkRoles = user.userRoles?.rolesByNetwork?.find(
      (nr) => nr.network.tagId === currentNetwork?.tagId || nr.network.id === currentNetwork?.id
    )
    const held = (networkRoles?.roles ?? []).filter((r) => r.role.id === roleId)
    if (held.length) holders.push({ user, held })
  }

  const where = (r: UserRole) => {
    const target = r.team
      ? { ...r.team, href: `/admin/teams/${r.team.id}` }
      : r.teamGroup
        ? { ...r.teamGroup, href: `/admin/team-groups/${r.teamGroup.id}` }
        : null
    if (!target) return <span>{t('roles.networkWide')}</span>
    return (
      <>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => navigate(target.href)}
        >
          {target.name || target.id}
        </Button>
        {target.objectState && target.objectState !== 'ACTIVE' && (
          <span className="text-xs text-muted-foreground">
            {' '}
            ({t(target.objectState === 'HIDDEN' ? 'roles.deleted' : 'roles.archived')})
          </span>
        )}
      </>
    )
  }

  return (
    <Card tone="people">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={User} />
          {t('roles.users', { count: holders.length })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : holders.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('roles.noUsers')}</p>
        ) : (
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-(--ad-border) text-left text-xs text-(--ad-text-muted)">
                <th className="px-3 py-1.5 font-semibold">{t('roles.user')}</th>
                <th className="px-3 py-1.5 font-semibold">{t('roles.heldOn')}</th>
              </tr>
            </thead>
            <tbody>
              {holders.map(({ user, held }) => (
                <tr key={user.id} className="border-b border-(--ad-border) last:border-b-0">
                  <td className="px-3 py-1.5">
                    {isMasked(user.username) ? (
                      <span>{user.username}</span>
                    ) : (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        {user.displayName || user.username}
                      </Button>
                    )}
                    {user.objectState === 'INACTIVE' && (
                      <span className="text-xs text-muted-foreground">
                        {' '}
                        ({t('roles.inactive')})
                      </span>
                    )}
                    {user.displayName && !isMasked(user.username) && (
                      <div className="text-xs text-muted-foreground">{user.username}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {held.map((r, i) => (
                      <Fragment key={r.team?.id ?? r.teamGroup?.id ?? 'network'}>
                        {i > 0 && ', '}
                        {where(r)}
                      </Fragment>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  )
}
